import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG =
  "https://images.unsplash.com/photo-1688891648910-1e056d2377c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBhYnN0cmFjdCUyMG5hdHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTY1OTg3fDA&ixlib=rb-4.1.0&q=85";

export default function TilbakestillPassord() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const welcome = params.get("velkommen") === "1";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!token) {
      setError("Ugyldig eller manglende lenke. Be om en ny tilbakestilling.");
      return;
    }
    if (password.length < 8) {
      setError("Passordet må ha minst 8 tegn.");
      return;
    }
    if (password !== confirm) {
      setError("Passordene er ikke like.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(welcome ? "Passordet er satt. Logg inn med e-posten din." : "Passordet er oppdatert. Logg inn med det nye passordet.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto w-full max-w-sm"
        >
          <Logo subtitle="Styrerom & Aksjeeierbok" />

          <div className="mt-12">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
              <KeyRound className="h-6 w-6" />
            </div>
            <h1 className="mt-6 font-headings text-3xl font-extrabold tracking-tight text-foreground">{welcome ? "Sett passordet ditt" : "Velg nytt passord"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{welcome ? "Velg et passord for kontoen din, så er du klar til å logge inn." : "Skriv inn et nytt passord for kontoen din."}</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="password" className="text-xs font-medium">Nytt passord</Label>
              <Input
                id="password" data-testid="reset-password-input" type="password" autoComplete="new-password"
                placeholder="min. 8 tegn" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-medium">Bekreft nytt passord</Label>
              <Input
                id="confirm" data-testid="reset-confirm-input" type="password" autoComplete="new-password"
                placeholder="gjenta" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>

            {error ? <p data-testid="reset-error" className="text-sm text-destructive">{error}</p> : null}

            <Button type="submit" data-testid="reset-submit-button" disabled={loading} className="h-11 w-full gap-2 text-sm font-semibold active:scale-[0.98]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {loading ? "Oppdaterer …" : "Oppdater passord"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/login" data-testid="reset-back-login-link" className="font-semibold text-primary hover:underline">Tilbake til innlogging</Link>
          </p>

          <p className="mt-10 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Kryptert · ISO 27001 · Data lagret i Norge
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/30 to-transparent" />
      </div>
    </div>
  );
}
